import { Controlled as CodeMirror2 } from 'react-codemirror2'
import React, { useEffect, useState } from 'react'
import { kdpMetrics, metrics, operators, properties } from './common'
import CodeMirror, { type Editor } from 'codemirror'
import { getTimePeriodTooltip } from './editor_util'
import { addSyntaxV2, MERCHJAR_V2_MODE } from './syntax_v2'
import { addSyntaxV1, MERCHJAR_V1_MODE } from './syntax_v1'
import {
    applyCustomFieldDecorations,
    buildCustomFieldCompletions,
} from '../../features/custom-fields/editor-language'
import type { CustomFieldEditorContext } from '../../features/custom-fields/types'

export {
    isDate,
    isMetric,
    isNumber,
    isOperator,
    isProperty,
    isRelativeDate,
    isStringValue,
} from './syntax_common'

// require('codemirror/lib/codemirror.css')
// require('codemirror/mode/javascript/javascript')
// require('codemirror/addon/edit/closebrackets')
// require('codemirror/addon/hint/show-hint')
// require('codemirror/theme/monokai.css')
// require('./merchjar-theme.scss')

// Mode registration is static and contains no catalog data. Register before
// react-codemirror mounts so its initial mode option always resolves.
addSyntaxV1()
addSyntaxV2()

export const InitialTriggerV1 = 'over the last 30 days acos > 20% and spend > $10'
export const InitialTriggerV2 = 'acos(30d) > 20% and cost(30d) > $10'

const initialTrigger = (version: number) => {
    if (version == 1) {
        return InitialTriggerV1
    } else {
        return InitialTriggerV2
    }
}

interface TriggerEditorProps {
    value: string
    onChange: (v: string) => void
    version: number
    onPreviewRefresh?: () => void
    height?: number
    customFieldContext?: CustomFieldEditorContext
}

// Custom hook for tooltip management
const useTooltipManager = (variableMap: { [key: string]: string }, version: number) => {
    // Helper function to create a tooltip element
    const createTooltip = (content: string, coords: { left: number; top: number }) => {
        const tooltip = document.createElement('div')
        tooltip.className = 'cm-tooltip'
        tooltip.textContent = content
        tooltip.style.position = 'absolute'
        tooltip.style.zIndex = '1000'
        tooltip.style.background = '#f8f8f8'
        tooltip.style.border = '1px solid #ddd'
        tooltip.style.padding = '2px 5px'
        tooltip.style.borderRadius = '3px'
        tooltip.style.pointerEvents = 'none'
        tooltip.style.fontSize = '12px'
        tooltip.style.fontFamily = 'monospace'
        tooltip.style.boxShadow = '0 1px 3px rgba(0,0,0,0.2)'
        tooltip.style.left = `${coords.left}px`
        tooltip.style.top = `${coords.top - 25}px`

        return tooltip
    }

    // Helper function to get variable value
    const getVariableValue = (variableName: string, editor: any, state: any) => {
        // First check our React state variable map
        if (variableMap[variableName]) {
            return variableMap[variableName]
        }

        // Then try to get the value from the CodeMirror state
        if (state && state.variableValues && state.variableValues[variableName]) {
            return state.variableValues[variableName]
        }

        // If not found in state, try to parse from the editor content
        const content = editor.getValue()
        const lines = content.split('\n')

        for (const line of lines) {
            const match = line.match(new RegExp(`let\\s+${variableName}\\s*=\\s*(.+?)\\s*;`))
            if (match && match[1]) {
                return match[1].trim()
            }
        }

        return null
    }

    // Setup tooltip handler for editor
    const setupTooltipHandler = (editor: any) => {
        let currentTooltip: HTMLElement | null = null

        // Function to remove any existing tooltip
        const removeTooltip = () => {
            if (currentTooltip && document.body.contains(currentTooltip)) {
                document.body.removeChild(currentTooltip)
                currentTooltip = null
            }
        }

        // Add mousemove listener to wrapper
        const wrapper = editor.getWrapperElement()
        wrapper.addEventListener('mousemove', (event: MouseEvent) => {
            // Remove any existing tooltip
            removeTooltip()

            // Get position from mouse coordinates
            const pos = editor.coordsChar({
                left: event.clientX,
                top: event.clientY,
            })

            // Get token at position
            const token = editor.getTokenAt(pos)

            // Get the state from the editor
            const state = editor.getStateAfter(pos.line)

            if (token && token.string && token.string.trim() !== '') {
                // Check for variable identifiers
                if (version === 2 && token.type === 'identifier') {
                    const variableValue = getVariableValue(token.string, editor, state)

                    if (variableValue) {
                        const tooltipContent = `${token.string} = ${variableValue}`
                        const coords = editor.charCoords(pos)

                        // Create and position tooltip
                        currentTooltip = createTooltip(tooltipContent, coords)

                        // Add tooltip to document
                        document.body.appendChild(currentTooltip)
                    }
                }
                // Check for time period patterns
                else if (token.type === 'def') {
                    // Find the parentheses boundary and collect tokens between them
                    const getTokensBetweenParentheses = (editor: any, pos: any, token: any) => {
                        const line = pos.line
                        const lineText = editor.getLine(line)
                        let openParenPos = null
                        let closeParenPos = null

                        // First find the opening parenthesis by scanning backward
                        for (let ch = token.start; ch >= 0; ch--) {
                            if (lineText[ch] === '(') {
                                openParenPos = { line, ch }
                                break
                            }
                        }

                        // Then find the closing parenthesis by scanning forward
                        for (let ch = token.end; ch < lineText.length; ch++) {
                            if (lineText[ch] === ')') {
                                closeParenPos = { line, ch }
                                break
                            }
                        }

                        // If we couldn't find both parentheses, return null
                        if (!openParenPos || !closeParenPos) {
                            return null
                        }

                        // Collect all tokens between the parentheses
                        const tokens = []
                        const currentPos = { line, ch: openParenPos.ch + 1 }

                        while (currentPos.ch < closeParenPos.ch) {
                            const currentToken = editor.getTokenAt(currentPos)

                            // Avoid infinite loop if token doesn't advance position
                            if (currentToken.end <= currentPos.ch) {
                                currentPos.ch += 1
                                continue
                            }

                            tokens.push(currentToken)
                            currentPos.ch = currentToken.end
                        }

                        // Create a string from all token strings
                        return {
                            tokens,
                            text: tokens.map((t) => t.string).join(''),
                            range: {
                                from: openParenPos,
                                to: closeParenPos,
                            },
                        }
                    }

                    // Get all tokens between parentheses
                    const parenthesesContent = getTokensBetweenParentheses(editor, pos, token)

                    // Pass the parentheses content to the tooltip function
                    const tooltipContent = getTimePeriodTooltip(
                        parenthesesContent?.text || token.string,
                    )
                    if (tooltipContent) {
                        const coords = editor.charCoords(pos)

                        // Create and position tooltip
                        currentTooltip = createTooltip(tooltipContent, coords)

                        // Add tooltip to document
                        document.body.appendChild(currentTooltip)
                    }
                }
            }
        })

        // Remove tooltip when mouse leaves the editor
        wrapper.addEventListener('mouseleave', removeTooltip)
    }

    return { setupTooltipHandler }
}

export const TriggerEditor = ({
    value,
    onChange,
    version = 1,
    onPreviewRefresh,
    height,
    customFieldContext,
}: TriggerEditorProps) => {
    const [internalValue, setInternalValue] = useState(value || initialTrigger(version))
    const [editorInstance, setEditorInstance] = useState<any>(null)
    const [variableMap, setVariableMap] = useState<{ [key: string]: string }>({})

    // Get tooltip manager
    const tooltipManager = useTooltipManager(variableMap, version)

    const mode = version === 1 ? MERCHJAR_V1_MODE : { name: MERCHJAR_V2_MODE, customFieldContext }

    // Register modes once and update only this editor instance. Catalog data is
    // parser configuration, never global mode state.
    useEffect(() => {
        addSyntaxV1()
        addSyntaxV2()
        editorInstance?.setOption('mode', mode)
        editorInstance?.refresh()
    }, [editorInstance, version, customFieldContext])

    useEffect(() => {
        if (!editorInstance || version !== 2) return
        return applyCustomFieldDecorations(editorInstance, customFieldContext)
    }, [editorInstance, internalValue, version, customFieldContext])

    useEffect(() => {
        if (value !== internalValue) {
            setInternalValue(value)
        }
    }, [value])

    // Parse variable declarations whenever the content changes
    useEffect(() => {
        if (version === 2) {
            const newVariableMap: { [key: string]: string } = {}
            const lines = internalValue.split('\n')

            for (const line of lines) {
                const match = line.match(/let\s+([a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*(.+?)\s*;/)
                if (match && match[1] && match[2]) {
                    newVariableMap[match[1]] = match[2].trim()
                }
            }

            setVariableMap(newVariableMap)
        }
    }, [internalValue, version])

    const getEditorHeight = (h?: number): string => {
        if (!h) {
            return '300px'
        }
        return `${h - 44}px`
    }

    // Handle height changes by forcing CodeMirror to refresh
    useEffect(() => {
        if (editorInstance && height) {
            // Set the size and force a refresh
            editorInstance.setSize(null, getEditorHeight(height))
            setTimeout(() => {
                editorInstance.refresh()
            }, 0)
        }
    }, [editorInstance, height])

    return (
        <CodeMirror2
            className=""
            value={internalValue}
            options={{
                lineNumbers: true,
                mode,
                // This gets disabled to fix an issue with the cursor jumping around
                // when opening a double quote or parenthesis.
                autoCloseBrackets: false,
                theme: 'merchjar',

                extraKeys: {
                    'Ctrl-Space': 'autocomplete',
                    'Ctrl-Enter': () => {
                        if (onPreviewRefresh) {
                            onPreviewRefresh()
                        }
                    },
                    'Cmd-Enter': () => {
                        if (onPreviewRefresh) {
                            onPreviewRefresh()
                        }
                    },
                },
                hintOptions: {
                    hint: (cm: Editor) => hints(cm, customFieldContext),
                    completeSingle: false,
                },
            }}
            onCursorActivity={(cm) => {
                cm.showHint()
            }}
            onBeforeChange={(editor, data, value) => {
                setInternalValue(value)
            }}
            onChange={(editor, data, value) => {
                onChange(value)
            }}
            // Add a ref to apply the hover tooltip manually after component mounts
            editorDidMount={(editor) => {
                setEditorInstance(editor)
                editor.setSize(null, getEditorHeight(height))

                // Setup tooltip handler
                tooltipManager.setupTooltipHandler(editor)

                // Disable resize on CodeMirror wrapper
                const wrapper = editor.getWrapperElement()
                wrapper.style.resize = 'none'

                // Also disable on any child elements that might be resizable
                const scrollElement = wrapper.querySelector('.CodeMirror-scroll')
                if (scrollElement) {
                    ;(scrollElement as HTMLElement).style.resize = 'none'
                }
            }}
        />
    )
}

export const hints = (cm: Editor, customFieldContext?: CustomFieldEditorContext) => {
    return new Promise<CodeMirror.Hints | null>((accept) => {
        const comp = [..._comp]
        if (window.isKdp) {
            comp[2] = [...comp[2], ...kdpMetrics]
        }

        setTimeout(() => {
            const cursor = cm.getCursor()
            const line = cm.getLine(cursor.line)
            const isPropertyPosition = (position: number) => {
                const tokenType = cm.getTokenAt(CodeMirror.Pos(cursor.line, position)).type
                if (
                    tokenType
                        ?.split(/\s+/)
                        .some((type) =>
                            ['comment2', 'comment-v2', 'string', 'time-period-v2'].includes(type),
                        )
                ) {
                    return false
                }
                const before = line.slice(0, position).trimEnd()
                return before === '' || before.endsWith('(') || /(?:^|\s)(and|or)$/i.test(before)
            }
            let start = cursor.ch
            let end = cursor.ch
            while (start && /[a-zA-Z_0-9><=!]/.test(line.charAt(start - 1))) {
                --start
            }
            while (end < line.length && /[a-zA-Z_0-9><=!]/.test(line.charAt(end))) {
                ++end
            }
            let word = line.slice(start, end).toLowerCase()

            let customStart = cursor.ch
            while (customStart && /[a-zA-Z_0-9.]/.test(line.charAt(customStart - 1))) {
                --customStart
            }
            const customWord = line.slice(customStart, cursor.ch)
            const customCompletions = buildCustomFieldCompletions(customWord, customFieldContext)
            if (customWord && customCompletions.length > 0) {
                return accept({
                    list: customCompletions,
                    from: CodeMirror.Pos(cursor.line, customStart),
                    to: CodeMirror.Pos(cursor.line, cursor.ch),
                })
            }

            if (word === 'last') {
                const tmp = line.slice(start, end + 10)
                const r = tmp.match(/last \d+ days/)
                if (r && r[0]) {
                    word = r[0]
                    end = start + r[0].length
                }
            } else if (word === 'days') {
                const tmp = line.slice(start - 10, end)
                const r = tmp.match(/last \d+ days/)
                if (r && r[0]) {
                    word = r[0]
                    start = end - r[0].length
                }
            } else if (/\d+/.test(word)) {
                const tmp = line.slice(start - 10, end + 10)
                const r = tmp.match(/last \d+ days/)
                if (r && r[0]) {
                    const index = r[0].indexOf(word)
                    word = r[0]
                    start = start - index
                    end = end + (word.length - index - 2)
                }
            }

            let propertyStart = start
            while (propertyStart && /[a-zA-Z_0-9. ]/.test(line.charAt(propertyStart - 1))) {
                --propertyStart
            }
            while (line.charAt(propertyStart) === ' ') {
                ++propertyStart
            }
            while (propertyStart < end) {
                const propertyWord = line.slice(propertyStart, end).toLowerCase()
                if (isPropertyPosition(propertyStart)) {
                    const existingProperty = properties.find(
                        (property) =>
                            line.slice(propertyStart, propertyStart + property.length) ===
                                property &&
                            cursor.ch >= propertyStart &&
                            cursor.ch <= propertyStart + property.length &&
                            !/[a-zA-Z_0-9]/.test(line.charAt(propertyStart + property.length)),
                    )
                    if (existingProperty) return accept(null)

                    const propertyCompletions = properties.filter((property) =>
                        property.startsWith(propertyWord),
                    )
                    if (
                        propertyCompletions.length > 0 &&
                        !propertyCompletions.includes(propertyWord)
                    ) {
                        return accept({
                            list: propertyCompletions,
                            from: CodeMirror.Pos(cursor.line, propertyStart),
                            to: CodeMirror.Pos(cursor.line, end),
                        })
                    }
                }

                const nextSpace = line.indexOf(' ', propertyStart)
                if (nextSpace === -1) break
                propertyStart = nextSpace + 1
                while (line.charAt(propertyStart) === ' ') {
                    ++propertyStart
                }
            }

            if (word.trim() !== '') {
                for (let i = 0; i < comp.length; i++) {
                    if (comp[i].findIndex((w) => w.startsWith(word)) !== -1) {
                        const fullMatch = comp[i].indexOf(word) !== -1
                        if (!fullMatch) {
                            return accept({
                                list: comp[i].filter((w) => w.startsWith(word)),
                                from: CodeMirror.Pos(cursor.line, start),
                                to: CodeMirror.Pos(cursor.line, end),
                            })
                        }
                    }
                }
            }

            return accept(null)
        }, 100)
    })
}

const _comp = [['last 30 days', 'lifetime'], [...metrics], [...operators], ['and', 'or']]
